import { useRef, useState, useCallback } from "react";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/i.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;

const DEFAULT_CAPTURE_ASPECT_RATIO = 16 / 9;

const TELEPHOTO_KEYWORDS = ["tele", "zoom", "3x", "5x", "10x", "periscope"];
const FRONT_KEYWORDS = ["front", "user", "selfie"];
const REAR_KEYWORDS = ["back", "rear", "environment", "wide", "ultra"];

const EXPOSURE_ANALYSIS_SIZE = 128;
let exposureAnalysisCanvas = null;
let exposureAnalysisContext = null;

const getExposureAnalysisContext = () => {
  if (exposureAnalysisContext) return exposureAnalysisContext;
  if (typeof document === "undefined") return null;

  exposureAnalysisCanvas = document.createElement("canvas");
  exposureAnalysisCanvas.width = EXPOSURE_ANALYSIS_SIZE;
  exposureAnalysisCanvas.height = EXPOSURE_ANALYSIS_SIZE;
  exposureAnalysisContext = exposureAnalysisCanvas.getContext("2d", {
    willReadFrequently: true,
  });

  return exposureAnalysisContext;
};

export const analyzePhotoUsability = async (
  blob,
  options = {}
) => {
  const { darkThreshold = 50, brightThreshold = 160 } = options;
  const ctx = getExposureAnalysisContext();
  if (!ctx) {
    console.log('Exposure analysis context not available');
    return { isUsable: true, reason: "good", avgLuminance: 0 };
  }

  let bitmap = null;
  try {
    bitmap = await createImageBitmap(blob);
    ctx.clearRect(0, 0, EXPOSURE_ANALYSIS_SIZE, EXPOSURE_ANALYSIS_SIZE);
    ctx.drawImage(bitmap, 0, 0, EXPOSURE_ANALYSIS_SIZE, EXPOSURE_ANALYSIS_SIZE);

    const { data } = ctx.getImageData(
      0,
      0,
      EXPOSURE_ANALYSIS_SIZE,
      EXPOSURE_ANALYSIS_SIZE
    );

    const pixelCount = EXPOSURE_ANALYSIS_SIZE * EXPOSURE_ANALYSIS_SIZE;
    let luminanceSum = 0;
    let blackCount = 0;
    let whiteCount = 0;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      luminanceSum += 0.299 * r + 0.587 * g + 0.114 * b;

      if (r === 0 && g === 0 && b === 0) {
        blackCount += 1;
      } else if (r === 255 && g === 255 && b === 255) {
        whiteCount += 1;
      }
    }

    const avgLuminance = luminanceSum / pixelCount;
    const blackRatio = blackCount / pixelCount;
    const whiteRatio = whiteCount / pixelCount;

    if (blackRatio > 0.1 || whiteRatio > 0.1) {
      return { isUsable: false, reason: "clipping", avgLuminance };
    }

    if (avgLuminance < darkThreshold) {
      return { isUsable: false, reason: "too_dark", avgLuminance };
    }

    if (avgLuminance > brightThreshold) {
      return { isUsable: false, reason: "too_bright", avgLuminance };
    }

    return { isUsable: true, reason: "good", avgLuminance };
  } finally {
    if (bitmap && typeof bitmap.close === "function") {
      bitmap.close();
    }
  }
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const imageBitmapToJpegBlob = (bitmap, quality = 0.95) =>
  new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("CanvasContextUnavailable"));
        return;
      }
      ctx.drawImage(bitmap, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("CanvasToBlobFailed"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        quality
      );
    } catch (err) {
      reject(err);
    }
  });

let cachedPreferredDeviceId = null;
const enforcePreviewConstraints = async (stream) => {
  const track = stream?.getVideoTracks?.()?.[0];
  if (!track?.applyConstraints) return;

  const candidates = [
    {
      aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
      resizeMode: "none",
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    {
      aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
      resizeMode: "none",
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    {
      aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
      resizeMode: "none",
      width: { ideal: 640 },
      height: { ideal: 360 },
    },
    {
      aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
    },
  ];

  for (const constraints of candidates) {
    try {
      await track.applyConstraints(constraints);
      return;
    } catch {
      // try next
    }
  }
};

const getVideoConstraints = (useFallback, preferredDeviceId) => {
  if (useFallback) {
    return {
      ...(preferredDeviceId ? { deviceId: { exact: preferredDeviceId } } : {}),
      facingMode: { ideal: "environment" },
      aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
      resizeMode: "none",
      width: { ideal: 1280 },
      height: { ideal: 720 },
    };
  }
  return {
    ...(preferredDeviceId ? { deviceId: { exact: preferredDeviceId } } : {}),
    facingMode: "environment",
    aspectRatio: { ideal: DEFAULT_CAPTURE_ASPECT_RATIO },
    resizeMode: "none",
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  };
};

export const useSecureCamera = (options = {}) => {
  const { 
    security = { blockVirtualDevices: true, requireHardwareFeatures: true },
    onSecurityError
  } = options;

  const videoRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [isFake, setIsFake] = useState(false);
  const [useFallbackConstraints, setUseFallbackConstraints] = useState(false);
  const [preferredDeviceId, setPreferredDeviceId] = useState(cachedPreferredDeviceId);
  const isSelectingCameraRef = useRef(false);

  const videoConstraints = getVideoConstraints(useFallbackConstraints, preferredDeviceId);

  const stopStream = useCallback(() => {
    const stream = videoRef.current?.stream;
    if (!stream) return;
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch {
        // ignore
      }
    }
  }, []);

  const loadVideoDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices
        .filter((d) => d.kind === "videoinput");
      return videoInputs;
    } catch {
      return [];
    }
  }, []);

  const probeCameraCandidates = useCallback(async (videoInputs = null) => {
    if (isSelectingCameraRef.current || !isAndroid) return [];
    isSelectingCameraRef.current = true;

    try {
      const devices = videoInputs?.length
        ? videoInputs
        : (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");

      if (devices.length <= 1) return [];

      const candidates = [];
      for (const device of devices) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: device.deviceId } },
            audio: false,
          });
          const testTrack = testStream.getVideoTracks()[0];
          const settings = testTrack?.getSettings?.() || {};
          const caps = testTrack?.getCapabilities?.() || {};
          const focusDistance = caps.focusDistance ?? null;
          const focusMin = typeof focusDistance === "object" && focusDistance
            ? focusDistance.min ?? null
            : null;
          const focusMax = typeof focusDistance === "object" && focusDistance
            ? focusDistance.max ?? null
            : null;
          const facing = settings.facingMode || "";
          const label = (device.label || "").toLowerCase();
          const isFront = facing === "user" || FRONT_KEYWORDS.some((kw) => label.includes(kw));
          const isRear = facing === "environment" || REAR_KEYWORDS.some((kw) => label.includes(kw)) || !facing;

          candidates.push({
            deviceId: device.deviceId,
            label: device.label,
            facingMode: facing,
            zoomMax: caps.zoom?.max ?? null,
            focusMin,
            focusMax,
            focusMode: caps.focusMode ?? null,
            isFront,
            isRear,
          });

          for (const track of testStream.getTracks()) {
            try {
              track.stop();
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore
        }
      }

      return candidates;
    } finally {
      isSelectingCameraRef.current = false;
    }
  }, []);

  const selectPreferredRearCamera = useCallback(async (deviceList = null) => {
    if (preferredDeviceId || !isAndroid) return;
    const devices = deviceList?.length ? deviceList : await loadVideoDevices();
    if (!devices.length) return;

    const wasStreamActive = videoRef.current?.stream;
    stopStream();
    const candidates = await probeCameraCandidates(devices);
    const rearCandidates = candidates.filter((c) => c.isRear && !c.isFront);
    const focusCandidates = rearCandidates.filter((c) =>
      Array.isArray(c.focusMode) &&
      (c.focusMode.includes("single-shot") || c.focusMode.includes("continuous"))
    );

    let preferred = null;
    if (focusCandidates.length) {
      preferred = focusCandidates[0];
    } else if (rearCandidates.length) {
      preferred = rearCandidates[0];
    }

    if (!preferred?.deviceId) {
      const fallback = devices.find((d) => {
        const dLabel = (d.label || "").toLowerCase();
        if (!dLabel) return false;
        if (FRONT_KEYWORDS.some((kw) => dLabel.includes(kw))) return false;
        if (TELEPHOTO_KEYWORDS.some((kw) => dLabel.includes(kw))) return false;
        return REAR_KEYWORDS.some((kw) => dLabel.includes(kw));
      });
      preferred = fallback ? { deviceId: fallback.deviceId } : null;
    }

    if (preferred?.deviceId) {
      cachedPreferredDeviceId = preferred.deviceId;
      setPreferredDeviceId(preferred.deviceId);
    } else if (wasStreamActive && candidates.length === 0) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false
        });
        if (videoRef.current) {
          videoRef.current.stream = stream;
        }
      } catch (err) {
        console.warn("Failed to restart stream after camera selection:", err);
      }
    }
  }, [preferredDeviceId, stopStream, probeCameraCandidates, loadVideoDevices]);

  const checkSecurity = useCallback(async (stream) => {
    try {
      setError(null);
      const track = stream.getVideoTracks()[0];
      const label = track.label.toLowerCase();

      enforcePreviewConstraints(stream);

      if (security.blockVirtualDevices) {
        const blockedKeywords = ["virtual", "obs", "manycam", "fake", "mjpeg", "irium"];
        if (blockedKeywords.some((kw) => label.includes(kw))) {
          setIsFake(true);
          if (onSecurityError) {
            onSecurityError("Virtual camera detected");
          }
          stopStream();
          return false;
        }
      }

      const devices = await loadVideoDevices();
      if (!preferredDeviceId) {
        await selectPreferredRearCamera(devices);
      }

      if (isMobile && security.requireHardwareFeatures) {
        const capabilities = track.getCapabilities ? track.getCapabilities() : {};
        const hasHardware = "focusMode" in capabilities || "zoom" in capabilities || "torch" in capabilities;
        
        if (!hasHardware) {
          console.warn("Hardware verification failed: Camera appears simulated.");
        }
      }

      setIsReady(true);
      return true;
    } catch (err) {
      console.error("checkSecurity failed:", err);
      setIsReady(true);
      return true;
    }
  }, [security, onSecurityError, selectPreferredRearCamera, stopStream, preferredDeviceId, probeCameraCandidates, loadVideoDevices]);

  const handleCameraError = useCallback((err) => {
    const name = err?.name || "CameraError";
    const message = err?.message || "Camera access failed.";
    
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      setError("permission_denied");
      setIsReady(true);
      return;
    }
    
    setError(`${name}: ${message}`);
    
    if (cachedPreferredDeviceId && !useFallbackConstraints) {
      cachedPreferredDeviceId = null;
      setPreferredDeviceId(null);
      setUseFallbackConstraints(true);
    } else if (!useFallbackConstraints) {
      setUseFallbackConstraints(true);
    } else {
      setIsReady(true);
    }
  }, [useFallbackConstraints]);

  const resetState = useCallback(() => {
    setIsFake(false);
    setError(null);
    setIsReady(false);
    setUseFallbackConstraints(false);
    setPreferredDeviceId(cachedPreferredDeviceId);
  }, []);

  const capture = useCallback(async () => {
    if (!videoRef.current) return null;

    try {
      const webcam = videoRef.current;
      if (!webcam) return null;

      const video = webcam.video;
      if (!video) return null;

      const stream = video.srcObject;
      const track = stream?.getVideoTracks()[0];
      if (!track) return null;

      const filename = `capture_${Date.now()}.jpg`;

      if ("ImageCapture" in window) {
        try {
          const imageCapture = new ImageCapture(track);

          let photoSettings;
          let photoCapabilities = null;
          try {
            photoCapabilities = await imageCapture.getPhotoCapabilities();

            if (photoCapabilities.imageHeight && photoCapabilities.imageWidth) {
              const maxW = photoCapabilities.imageWidth.max;
              const minW = photoCapabilities.imageWidth.min;
              const maxH = photoCapabilities.imageHeight.max;
              const minH = photoCapabilities.imageHeight.min;

              const tryWidth = maxW;
              const tryHeight = Math.round(tryWidth / DEFAULT_CAPTURE_ASPECT_RATIO);

              let imageWidth = tryWidth;
              let imageHeight = tryHeight;

              if (imageHeight > maxH || imageHeight < minH) {
                const altHeight = maxH;
                const altWidth = Math.round(altHeight * DEFAULT_CAPTURE_ASPECT_RATIO);
                imageWidth = Math.min(maxW, Math.max(minW, altWidth));
                imageHeight = altHeight;
              }

              photoSettings = {
                imageWidth,
                imageHeight,
              };
            }
          } catch {
            // Use default settings if capabilities unavailable
          }
          
          let blob = await imageCapture.takePhoto(photoSettings);

          const analysis = await analyzePhotoUsability(blob);
          if (!analysis.isUsable && typeof imageCapture.grabFrame === "function") {
            let frameBitmap = null;
            try {
              frameBitmap = await imageCapture.grabFrame();
              const frameBlob = await imageBitmapToJpegBlob(frameBitmap);
              const frameAnalysis = await analyzePhotoUsability(frameBlob);
              if (frameAnalysis.isUsable) {
                blob = frameBlob;
              }
            } finally {
              if (frameBitmap && typeof frameBitmap.close === "function") {
                frameBitmap.close();
              }
            }
          }

          const file = new File([blob], filename, { type: "image/jpeg" });
          const base64 = await blobToBase64(blob);

          return { file, base64 };
        } catch (err) {
          console.warn("ImageCapture API failed, falling back to Canvas", err);
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) return null;
      ctx.drawImage(video, 0, 0);

      const base64 = canvas.toDataURL("image/jpeg", 0.95);

      const res = await fetch(base64);
      const blob = await res.blob();
      const file = new File([blob], filename, { type: "image/jpeg" });

      return { file, base64 };
    } catch (e) {
      console.error("Capture failed", e);
      return null;
    }
  }, []);

  return {
    videoRef,
    isReady,
    setIsReady,
    error,
    setError,
    isFake,
    capture,
    checkSecurity,
    handleCameraError,
    stopStream,
    resetState,
    videoConstraints,
    useFallbackConstraints,
    preferredDeviceId,
  };
};
